// src/components/ContactSection.js
import React from 'react';

const ContactSection = ({ 
  contacts, 
  primaryColor,
  language = 'en' // 'en' or 'km'
}) => {
  // Translations
  const translations = {
    en: {
      contactUs: 'Contact Us',
      connectWithUs: 'Connect with us through any of these platforms. We\'re here to help!',
      responseTime: 'Response time: Usually within 24 hours',
      contact: 'Contact'
    },
    km: {
      contactUs: 'ទាក់ទងពួកយើង',
      connectWithUs: 'ទាក់ទងមកយើងតាមរយៈបណ្តាញណាមួយខាងក្រោម។ យើងខ្ញុំរីករាយជួយអ្នក!',
      responseTime: 'ពេលវេលាឆ្លើយតប៖ ជាធម្មតាក្នុងរយៈពេល ២៤ ម៉ោង',
      contact: 'ទាក់ទង'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  if (!contacts || contacts.length === 0) {
    return null;
  }

  const getIconClass = (platform) => {
    const icons = {
      facebook: 'fab fa-facebook',
      telegram: 'fab fa-telegram',
      phone: 'fas fa-phone',
      tiktok: 'fab fa-tiktok',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      twitter: 'fab fa-twitter',
      youtube: 'fab fa-youtube',
      messenger: 'fab fa-facebook-messenger',
      line: 'fab fa-line',
      wechat: 'fab fa-weixin',
      viber: 'fab fa-viber',
      email: 'fas fa-envelope',
      shopee: 'fas fa-store',
      lazada: 'fas fa-shopping-cart'
    };
    return icons[platform?.toLowerCase()] || 'fas fa-link';
  };

  // Get platform color
  const getPlatformColor = (platform) => {
    const colors = {
      facebook: '#1877F2',
      telegram: '#0088cc',
      phone: '#25D366',
      tiktok: '#000000',
      instagram: '#E4405F',
      whatsapp: '#25D366',
      twitter: '#1DA1F2',
      youtube: '#FF0000',
      messenger: '#00B2FF',
      line: '#00C300',
      wechat: '#07C160',
      viber: '#7360F2',
      email: '#EA4335'
    };
    return colors[platform?.toLowerCase()] || primaryColor;
  };

  return (
    <div 
      id="contact" 
      className="bg-gradient-to-b from-gray-50 to-white py-16 md:py-20 mt-8"
      style={{ fontFamily: getFontFamily() }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
            <i className="fas fa-headset text-2xl" style={{ color: primaryColor }}></i>
          </div>
          <h2 
            className="text-3xl md:text-4xl font-semibold tracking-tight mb-3"
            style={{ color: primaryColor, fontFamily: getFontFamily() }}
          >
            {t.contactUs}
          </h2>
          <p 
            className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto"
            style={{ fontFamily: getFontFamily() }}
          >
            {t.connectWithUs}
          </p>
        </div>

        {/* Contact Buttons Grid */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {contacts.map((contact) => {
            const platformColor = getPlatformColor(contact.platform);
            return (
              <a
                key={contact.id}
                href={contact.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center space-x-2 md:space-x-3 px-5 md:px-7 py-3 md:py-3.5 rounded-full transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 active:scale-95"
                style={{ 
                  backgroundColor: platformColor,
                  fontFamily: getFontFamily()
                }}
              >
                <i className={`${getIconClass(contact.platform)} text-white text-base md:text-lg`}></i>
                <span 
                  className="text-white font-medium capitalize text-sm md:text-base tracking-wide"
                  style={{ fontFamily: getFontFamily() }}
                >
                  {contact.platform}
                </span>
                <i className="fas fa-arrow-right text-white/60 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
              </a>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-10 pt-6 border-t border-gray-200/80">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-gray-400 text-xs md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="fas fa-clock text-gray-300"></i>
              {t.responseTime}
            </span>
            <span className="hidden sm:block text-gray-300">|</span>
            <span className="flex items-center gap-1.5">
              <i className="fas fa-shield-alt text-gray-300"></i>
              {language === 'km' ? 'ធានាសុវត្ថិភាព' : 'Secure & Private'}
            </span>
            <span className="hidden sm:block text-gray-300">|</span>
            <span className="flex items-center gap-1.5">
              <i className="fas fa-smile text-gray-300"></i>
              {language === 'km' ? 'សេវាកម្មរហ័ស' : 'Quick Response'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;